/**
 * Copyright 2025 Lumavec Group LLC
 *
 * Templates API
 * 
 * This module provides API functions for managing architecture templates.
 * Templates enable creating new projects with pre-configured scaffolding.
 */

import { buildProductApiUrl } from './base';
import type {
  TemplateMetadata,
  TemplateOptions,
  AppManifest,
  ManifestValidationResult,
  TemplatesListResult,
  TemplateResult,
  GenerateManifestResult,
  ValidateManifestResult,
  InitializeProjectRequest,
  InitializeProjectResult,
  UpdateSharedCodeResult,
  CheckRepoInitializedResult,
  ApplyTemplateRequest,
  ApplyTemplateResult,
} from '../../../server/types';

// ============================================================================
// List & Get Operations
// ============================================================================

/**
 * List all available templates
 */
export async function listTemplates(): Promise<TemplatesListResult> {
  try {
    const url = buildProductApiUrl('/api/templates');
    const response = await fetch(url);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
      };
    }
    
    return await response.json();
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch templates',
    };
  }
}

/**
 * Get a single template by name/id
 */
export async function getTemplate(templateId: string): Promise<TemplateResult> {
  try {
    const url = buildProductApiUrl(`/api/templates/${templateId}`);
    const response = await fetch(url);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
      };
    }
    
    return await response.json();
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch template',
    };
  }
}

// ============================================================================
// Project Initialization
// ============================================================================

/**
 * Initialize a new project from a template
 */
export async function initializeProject(
  request: InitializeProjectRequest
): Promise<InitializeProjectResult> {
  try {
    const url = buildProductApiUrl('/api/templates/initialize');
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
      };
    }
    
    return await response.json();
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to initialize project',
    };
  }
}

// ============================================================================
// App Manifest Operations
// ============================================================================

/**
 * Generate an app manifest for a template with given options
 */
export async function generateManifest(
  templateId: string,
  projectName: string,
  options?: TemplateOptions
): Promise<GenerateManifestResult> {
  try {
    const url = buildProductApiUrl(`/api/templates/${templateId}/manifest`);
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ projectName, options: options || {} }),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
      };
    }
    
    return await response.json();
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to generate manifest',
    };
  }
}

/**
 * Validate an app manifest
 */
export async function validateManifest(
  manifest: AppManifest
): Promise<ValidateManifestResult> {
  try {
    const url = buildProductApiUrl('/api/templates/validate-manifest');
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(manifest),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
      };
    }
    
    return await response.json();
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to validate manifest',
    };
  }
}

// ============================================================================
// Shared Code Operations
// ============================================================================

/**
 * Update shared code in an existing project
 */
export async function updateSharedCode(
  projectPath: string
): Promise<UpdateSharedCodeResult> {
  try {
    const url = buildProductApiUrl('/api/templates/update-shared');
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ projectPath }),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
      };
    }
    
    return await response.json();
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to update shared code',
    };
  }
}

// ============================================================================
// Repository Initialization Operations
// ============================================================================

/**
 * Check if a repository has been initialized with a template
 * 
 * @param repoPath - Absolute path to the repository worktree (supports ~ prefix)
 * @returns Information about the repo's initialization status
 */
export async function checkRepoInitialized(
  repoPath: string
): Promise<CheckRepoInitializedResult> {
  try {
    const url = buildProductApiUrl(`/api/templates/check-initialized?repoPath=${encodeURIComponent(repoPath)}`);
    const response = await fetch(url);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        initialized: false,
        hasPackageJson: false,
        hasAppManifest: false,
        fileCount: 0,
        error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
      };
    }
    
    return await response.json();
  } catch (error) {
    return {
      success: false,
      initialized: false,
      hasPackageJson: false,
      hasAppManifest: false,
      fileCount: 0,
      error: error instanceof Error ? error.message : 'Failed to check repository initialization',
    };
  }
}

/**
 * Apply a template to an existing repository
 * 
 * This function writes template files to an existing blank repository
 * and commits them.
 * 
 * @param request - Apply template request with repoPath, template, projectName, and options
 * @returns Result of the operation including commit SHA
 */
export async function applyTemplate(
  request: ApplyTemplateRequest
): Promise<ApplyTemplateResult> {
  try {
    const url = buildProductApiUrl('/api/templates/apply');
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
      };
    }
    
    return await response.json();
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to apply template',
    };
  }
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get display label for template category
 */
export function getTemplateCategoryLabel(category: string): string {
  switch (category) {
    case 'web': return 'Web Application';
    case 'desktop': return 'Desktop Application';
    case 'platform': return 'Platform Application';
    case 'mobile': return 'Mobile Application';
    case 'api': return 'API Service';
    default: return category;
  }
}

/**
 * Get icon name for template category (for use with lucide-react)
 */
export function getTemplateCategoryIcon(category: string): string {
  switch (category) {
    case 'web': return 'Globe';
    case 'desktop': return 'Monitor';
    case 'platform': return 'Layers';
    case 'mobile': return 'Smartphone';
    case 'api': return 'Server';
    default: return 'FileCode';
  }
}

/**
 * Get recommended template for a given use case
 */
export function getRecommendedTemplate(
  templates: TemplateMetadata[],
  useCase: 'simple-web' | 'full-stack' | 'desktop' | 'platform-integrated'
): TemplateMetadata | undefined {
  switch (useCase) {
    case 'simple-web':
      return templates.find(t => t.name === 'web-spa');
    case 'full-stack':
      return templates.find(t => t.name === 'web-app');
    case 'desktop':
      return templates.find(t => t.name === 'desktop-app');
    case 'platform-integrated':
      return templates.find(t => t.name === 'platform-app');
    default:
      return templates.find(t => t.name === 'web-app');
  }
}

/**
 * Format estimated monthly cost
 */
export function formatEstimatedCost(cost: number): string {
  return `$${cost.toFixed(0)}/mo`;
}

/**
 * Get the default options for a template
 */
export function getDefaultOptions(template: TemplateMetadata): TemplateOptions {
  const options: TemplateOptions = {};
  
  for (const [key, option] of Object.entries(template.options)) {
    if (option.type === 'boolean') {
      (options as Record<string, boolean>)[key] = option.default as boolean;
    } else if (option.type === 'select') {
      (options as Record<string, string>)[key] = option.default as string;
    } else if (option.type === 'multiselect') {
      (options as Record<string, string[]>)[key] = option.default as string[];
    }
  }
  
  return options;
}

/**
 * Check if a template supports a specific feature
 */
export function templateSupportsFeature(
  template: TemplateMetadata,
  feature: keyof TemplateMetadata['features']
): boolean {
  return template.features[feature] === true;
}

